import React, { useState, useEffect } from "react";
import "./FlsScrollToLandingButton.css";

const FlsScrollToLandingButton = ({ targetId = "fls-landing-section" }) => {
  const [isScrolledToLanding, setIsScrolledToLanding] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Track scroll position to flip arrow direction (down / up)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.pageYOffset || window.scrollY || 0;

      // When near top of page, always default to scroll-down button ("About FLS")
      if (currentScrollY < 250) {
        setIsScrolledToLanding(false);
        return;
      }

      const landingElement = document.getElementById(targetId) || document.querySelector(".flsLandingContainer");
      if (landingElement) {
        const rect = landingElement.getBoundingClientRect();
        const headerOffset = window.innerWidth <= 768 ? 80 : 120;
        if (rect.top <= headerOffset + 100) {
          setIsScrolledToLanding(true);
        } else {
          setIsScrolledToLanding(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });
    handleScroll();

    // Re-check after dynamic content loads on page
    const timer = setTimeout(handleScroll, 800);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      clearTimeout(timer);
    };
  }, [targetId]);

  // 3-second inactivity auto-hide timer & activity listeners
  useEffect(() => {
    let timer = null;

    const resetInactivityTimer = () => {
      setIsVisible(true);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        setIsVisible(false);
      }, 3000);
    };

    const handleActivity = () => {
      resetInactivityTimer();
    };

    const handleWindowMouseLeave = (e) => {
      if (!e.relatedTarget && !e.toElement) {
        setIsVisible(false);
        if (timer) clearTimeout(timer);
      }
    };

    window.addEventListener("mousemove", handleActivity, { passive: true });
    window.addEventListener("scroll", handleActivity, { passive: true });
    window.addEventListener("touchstart", handleActivity, { passive: true });
    document.addEventListener("mouseleave", handleWindowMouseLeave);

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("scroll", handleActivity);
      window.removeEventListener("touchstart", handleActivity);
      document.removeEventListener("mouseleave", handleWindowMouseLeave);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const handleClick = () => {
    if (isScrolledToLanding) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      const landingElement = document.getElementById(targetId) || document.querySelector(".flsLandingContainer");
      if (landingElement) {
        const headerOffset = window.innerWidth <= 768 ? 70 : 100;
        const elementPosition = landingElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: Math.max(0, offsetPosition),
          behavior: "smooth",
        });
      }
    }
  };

  const label = isScrolledToLanding ? "Back to Top" : "About FLS";

  return (
    <div
      className={`fls-scroll-to-landing-container ${isVisible ? "visible" : ""}`}
      onMouseEnter={() => setIsVisible(true)}
    >
      <div className="fls-scroll-to-landing-badge">{label}</div>
      <button
        type="button"
        className="fls-scroll-to-landing-btn"
        onClick={handleClick}
        aria-label={label}
      >
        <svg
          className="fls-scroll-down-arrow"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {isScrolledToLanding ? (
            <>
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </>
          ) : (
            <>
              <line x1="12" y1="5" x2="12" y2="19" />
              <polyline points="19 12 12 19 5 12" />
            </>
          )}
        </svg>
      </button>
    </div>
  );
};

export default FlsScrollToLandingButton;
